import { useQueryClient } from '@tanstack/react-query'
import { mStockQueryKeys, useCurrentPricesQuery } from '@queries/mStock/mStockQueries.js'
import { useMStockPolling } from '@hooks/useMStockPolling.js'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTimeStore } from '@store/common/useTimeStore.js'
import { hasQueryData } from '@utils/hasQueryData.js'

const CURRENT_PRICE_POLLING_MS = 5000
const EMPTY_PRICE_MAP = {}

export const useCurrentPrice = (assetIds = [], enabled = true) => {
  const queryClient = useQueryClient()

  const { data, refetch, isError, isLoading } = useCurrentPricesQuery(assetIds, enabled)

  const assetIdsRef = useRef(assetIds)
  assetIdsRef.current = assetIds

  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  const isMountedRef = useRef(false)
  const lastRequestedBoundaryRef = useRef(null)
  const hasMissedBoundaryRef = useRef(false)

  const handleRefetch = useCallback(
    function requestRefetch() {
      if (!enabledRef.current) return

      if (!assetIdsRef.current.length) return

      const nowInServerTime = useTimeStore.getState().getBaseTime().getTime()
      const boundary =
        Math.floor(nowInServerTime / CURRENT_PRICE_POLLING_MS) * CURRENT_PRICE_POLLING_MS

      // 중복 요청 방지
      if (lastRequestedBoundaryRef.current === boundary) return

      const fetchStatus = queryClient.getQueryState(
        mStockQueryKeys.coinPrice(assetIdsRef.current)
      )?.fetchStatus

      if (fetchStatus === 'fetching') {
        if (hasMissedBoundaryRef.current) return

        hasMissedBoundaryRef.current = true

        refetch({ cancelRefetch: false }).finally(() => {
          hasMissedBoundaryRef.current = false

          if (!isMountedRef.current) return

          requestRefetch()
        })
        return
      }

      lastRequestedBoundaryRef.current = boundary

      refetch({ cancelRefetch: false })
    },
    [queryClient, refetch]
  )

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  useMStockPolling(handleRefetch, '5s', { enabled })

  const prices = useMemo(() => {
    if (!data?.length) return EMPTY_PRICE_MAP

    return Object.fromEntries(data.map((price) => [price.assetId, price]))
  }, [data])

  const hasData = hasQueryData(prices)

  return useMemo(
    () => ({
      prices,
      isError: isError && !hasData,
      isLoading: isLoading && !hasData,
      refetch
    }),
    [prices, isError, isLoading, hasData, refetch]
  )
}
